'use client'

import { SendIcon } from '@repo/ui/SendIcon'
import { useState, useEffect } from 'react'
import MUIBox from '@mui/material/Box'
import MUIButton from '@mui/material/Button'
import MUIStack from '@mui/material/Stack'
import MUITextField from '@mui/material/TextField'
import { useTheme } from '@mui/material/styles'

interface ChatFormProps {
  onSend: (message: string) => void
  isEditMode?: boolean
  initialMessage?: string
  onCancel?: () => void
}

export const ChatForm = ({ onSend, isEditMode = false, initialMessage = '', onCancel }: ChatFormProps) => {
  const [message, setMessage] = useState(initialMessage)

  const theme = useTheme()

  useEffect(() => {
    setMessage(initialMessage)
  }, [initialMessage])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (message.trim()) {
      onSend(message.trim())
      setMessage('')
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
    if (e.key === 'Escape' && isEditMode && onCancel) {
      onCancel()
    }
  }

  return (
    <MUIBox
      component="form"
      data-testid="ChatForm"
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        gap: 2,
        alignItems: 'flex-end',
        width: '100%',
        flexDirection: 'column'
      }}
    >
      <MUITextField
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Add reply"
        multiline
        minRows={3}
        maxRows={8}
        sx={{
          flex: 1,
          width: '100%',
          '& .MuiInputBase-root': {
            backgroundColor: 'white'
          }
        }}
      />
      {isEditMode ? (
        <MUIStack direction="row" spacing={2} justifyContent="flex-end">
          <MUIButton variant="text" onClick={onCancel} sx={{ minWidth: '80px', height: '40px', m: 0 }}>
            Cancel
          </MUIButton>
          <MUIButton
            type="submit"
            variant="contained"
            disabled={!message.trim()}
            sx={{ minWidth: '80px', height: '40px', m: 0 }}
          >
            EDIT
          </MUIButton>
        </MUIStack>
      ) : (
        <MUIButton
          type="submit"
          variant="contained"
          disabled={!message.trim()}
          sx={{
            minWidth: '80px',
            height: '40px',
            m: 0,
            backgroundColor: theme.palette.success.main,
            '&:hover': {
              backgroundColor: theme.palette.success.dark
            }
          }}
        >
          SEND
        </MUIButton>
      )}
    </MUIBox>
  )
}
